import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Code2, ShoppingCart, Globe, Headphones, ArrowRight, Check } from 'lucide-react';

const services = [
  {
    icon: Globe,
    title: 'Website Design',
    description: 'Custom website design and development tailored to your brand',
    path: '/contact',
    features: ['Custom design', 'Responsive layouts', 'Modern frameworks', 'SEO optimized'],
  },
  {
    icon: Headphones,
    title: 'IT Support',
    description: 'Ongoing technical support for your business systems',
    path: '/contact',
    features: ['Priority support', 'System maintenance', 'Bug fixes', 'Security updates'],
  },
  {
    icon: Code2,
    title: 'IT Consulting',
    description: 'Strategic technology guidance for your business',
    path: '/contact',
    features: ['Architecture planning', 'Technology selection', 'Process optimization', 'Security heath checks'],
  },
  {
    icon: ShoppingCart,
    title: 'eCommerce',
    description: 'Complete e-commerce solutions for online stores',
    path: '/contact',
    features: ['Platform setup', 'Payment integration', 'Inventory management', 'Custom features'],
  },
];

const pricingTiers = [
  {
    name: 'Technical Support',
    rate: 'AU$180/hr',
    description: 'General technical support and maintenance',
    features: [
      'Computer Issues',
      'Security updates',
      'Bug fixes',
      'Email support',
    ],
  },
  {
    name: 'Application Dev Support',
    rate: 'AU$220/hr',
    description: 'Custom development and feature implementation',
    features: [
      'New feature development',
      'Code refactoring',
      'API integrations',
      'Architecture consultation',
    ],
  },
  {
    name: 'Website Development',
    rate: 'Starting from $1997',
    description: 'New web development projects',
    features: [
      'Full project lifecycle',
      'Modern tech stack',
      'Custom design',
      'Post-launch support',
    ],
  },
];

export default function Services() {
  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-night-900 via-night-800 to-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our <span className="bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-xl text-offwhite/80 max-w-3xl mx-auto">
              Comprehensive technical solutions for modern businesses
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-night-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-night-700 rounded-xl p-8 hover:bg-night-600 transition-colors"
                initial={{ opacity: 1, y: 0 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <service.icon className="w-16 h-16 text-launch-blue mb-6" />
                <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                <p className="text-offwhite/70 mb-6">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center text-offwhite/80">
                      <Check size={16} className="text-launch-blue mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-launch-blue hover:text-launch-purple transition-colors"
                >
                  Contact Us
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-night-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Pricing</h2>
            <p className="text-offwhite/70 text-lg">Transparent hourly rates, ex GST</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                className="bg-night-700 rounded-xl p-8 border-2 border-night-600 hover:border-launch-blue transition-colors"
                initial={{ opacity: 1, y: 0 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <div className="text-4xl font-bold text-launch-blue mb-4">
                  {tier.rate}
                  <span className="text-lg text-offwhite/60"> ex GST</span>
                </div>
                <p className="text-offwhite/70 mb-6">{tier.description}</p>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check size={20} className="text-launch-blue mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-offwhite/80">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="block w-full text-center px-6 py-3 rounded-lg bg-gradient-to-r from-launch-blue to-launch-purple text-white font-medium hover:opacity-90 transition-opacity"
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-offwhite/60">
              Retainer packages available with SLA agreements. Contact us for custom pricing.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-launch-blue via-launch-purple to-launch-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-white/90 text-xl mb-8">
              Let's discuss your project and find the right solution for your needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-3 rounded-lg bg-white text-launch-blue font-medium hover:bg-offwhite"
            >
              Contact Us
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
